import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import ListenButton from "../components/ListenButton";
import { useAuth } from "../contexts/AuthContext";

const LEVEL_TINT = {
    Beginner: "#2D5D4E",
    Developing: "#1F4237",
    Intermediate: "#D47A55",
    Advanced: "#7D2F2F",
};

const PILLAR_EXPLAINER = {
    "phonemic-awareness": {
        what: "What we measured: whether you can hear and play with the smallest sounds in spoken words — the building blocks every reader uses.",
        if_weak: "Focus area: listening practice. Spend a few minutes a day blending and segmenting sounds out loud. This is the single strongest predictor of reading growth.",
        if_strong: "You have a great ear for sounds. That foundation will make every later step quicker.",
    },
    "phonics": {
        what: "What we measured: whether you can connect letters to sounds and decode words you've never seen before — from short CVC words to longer, multi-syllable ones.",
        if_weak: "Focus area: the letter-sound code. Work through phonics lessons in order; the Word Builder and Letter & Sound games will lock the patterns in.",
        if_strong: "You can decode confidently. Most adult reading struggles disappear at this stage.",
    },
    "fluency": {
        what: "What we measured: whether you can read common words instantly (without sounding them out) and in natural phrases, not one word at a time.",
        if_weak: "Focus area: sight word automaticity and reading aloud in phrase chunks. The Sight Words Sprint game gives you immediate measurable progress.",
        if_strong: "Your reading flows. That smoothness frees your brain to focus on meaning.",
    },
    "vocabulary": {
        what: "What we measured: whether you understand the meaning of common, intermediate, and academic words — including how to figure out new words from context.",
        if_weak: "Focus area: context clues, prefixes and suffixes, and academic vocabulary. Learning ten new useful words a week pays for itself fast.",
        if_strong: "You bring strong word knowledge. That's the silent power behind every confident reader.",
    },
    "comprehension": {
        what: "What we measured: whether you can pull the main idea from a paragraph, follow a sequence, and read between the lines for what the writer implies.",
        if_weak: "Focus area: active reading. Practice asking a question before each paragraph and summarizing it in one sentence after.",
        if_strong: "You understand what you read, not just what it says. That's the whole game.",
    },
};

export default function Assessment() {
    const navigate = useNavigate();
    const { refresh, user } = useAuth();
    const [questions, setQuestions] = useState([]);
    const [idx, setIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get("/assessment").then((r) => setQuestions(r.data.questions));
    }, []);

    // Read-aloud assessment: whenever a new question appears, speak it out
    // loud automatically via speechSynthesis. Struggling readers never have
    // to read a prompt themselves. Uses the same browser TTS as the Reading
    // Assistant so there's no server round-trip and no LLM credits spent.
    useEffect(() => {
        if (result) return;                    // don't read the results page
        const q = questions[idx];
        if (!q) return;
        if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
        window.speechSynthesis.cancel();
        const script = [q.prompt, q.word, q.passage, q.question]
            .filter(Boolean)
            .join(". ");
        if (!script.trim()) return;
        const u = new SpeechSynthesisUtterance(script);
        u.rate = 0.95;                          // slightly slower for the assessment
        // Nudge onto the event loop so the browser has the DOM settled;
        // otherwise Safari sometimes swallows the first utterance.
        const t = setTimeout(() => window.speechSynthesis.speak(u), 250);
        return () => {
            clearTimeout(t);
            window.speechSynthesis.cancel();
        };
    }, [idx, questions, result]);

    if (!questions.length && !result) {
        return <main className="container-page py-16">Loading…</main>;
    }

    const current = questions[idx];
    const isLast = idx === questions.length - 1;
    const pick = (oi) => setAnswers((a) => ({ ...a, [current.id]: oi }));
    const picked = answers[current?.id];

    const next = () => {
        if (picked === undefined) {
            toast.message("Pick an answer to continue");
            return;
        }
        setIdx(idx + 1);
    };

    const submit = async () => {
        if (picked === undefined) {
            toast.message("Pick an answer to continue");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/assessment/submit", { answers });
            setResult(data);
            refresh();
        } catch {
            toast.error("Could not save your results. Try once more.");
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- RESULT VIEW ----------
    if (result) {
        const pct = Math.round((result.total_correct / result.total_questions) * 100);
        const tint = LEVEL_TINT[result.level_label] || "#2D5D4E";

        return (
            <main className="container-page py-12 max-w-3xl fade-up" data-testid="assessment-result">
                <p className="label-eyebrow mb-2">Your placement</p>
                <h1 className="text-4xl font-semibold text-[#1C2321] mb-3" style={{ fontFamily: "Outfit" }} data-testid="assessment-result-title">
                    Here is where you start.
                </h1>
                <p className="text-lg text-[#515B57] mb-8">
                    You got {result.total_correct} of {result.total_questions} correct ({pct}%). That tells us exactly the right place to begin.
                </p>

                <div className="card-rw p-8 mb-8" style={{ borderTop: `6px solid ${tint}` }} data-testid="assessment-recommendation">
                    <p className="label-eyebrow mb-2" style={{ color: tint }}>Recommended level · {result.level_label}</p>
                    <h2 className="text-3xl font-medium mb-4" style={{ fontFamily: "Outfit" }}>
                        Start with {result.recommended_pillar_title}
                    </h2>
                    <p className="text-[#515B57] text-lg leading-relaxed mb-6">{result.message}</p>
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() => navigate(`/app/curriculum/${result.recommended_pillar_id}/lesson/${result.recommended_lesson_id}`)}
                            data-testid="assessment-start-first-lesson"
                            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237]"
                        >
                            Begin first lesson <ArrowRight className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => navigate(`/app/curriculum/${result.recommended_pillar_id}`)}
                            data-testid="assessment-see-pillar"
                            className="px-7 py-3.5 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] text-lg font-medium hover:bg-[#E8E2D2]"
                        >
                            See the pillar overview
                        </button>
                    </div>
                </div>

                <section>
                    <p className="label-eyebrow mb-3">How you did on each pillar</p>
                    <div className="space-y-3">
                        {result.breakdown.map((b) => {
                            const score = b.total === 0 ? 0 : Math.round((b.correct / b.total) * 100);
                            const strong = score >= 70;
                            const ex = PILLAR_EXPLAINER[b.pillar_id];
                            return (
                                <details key={b.pillar_id} className="card-rw p-5 group" data-testid={`assessment-breakdown-${b.pillar_id}`}>
                                    <summary className="flex items-center gap-5 cursor-pointer list-none">
                                        <div className="flex-1">
                                            <p className="font-medium text-[#1C2321]">{b.pillar_title}</p>
                                            <div className="w-full h-2 bg-[#E5E7E2] rounded-full mt-2 overflow-hidden">
                                                <div className="h-full" style={{ width: `${score}%`, backgroundColor: strong ? "#3A7D5C" : "#D47A55" }} />
                                            </div>
                                        </div>
                                        <span className="text-sm text-[#515B57] tabular-nums w-16 text-right">
                                            {b.correct} / {b.total}
                                        </span>
                                        <ChevronDown className="w-5 h-5 text-[#515B57] transition-transform group-open:rotate-180" strokeWidth={1.6} />
                                    </summary>
                                    {ex && (
                                        <div className="mt-5 pt-5 border-t border-[#E5E7E2] space-y-3" data-testid={`assessment-explainer-${b.pillar_id}`}>
                                            <p className="text-[#1C2321] leading-relaxed">{ex.what}</p>
                                            <p
                                                className="text-[#515B57] leading-relaxed pl-4 border-l-2"
                                                style={{ borderColor: strong ? "#3A7D5C" : "#D47A55" }}
                                            >
                                                {strong ? ex.if_strong : ex.if_weak}
                                            </p>
                                        </div>
                                    )}
                                </details>
                            );
                        })}
                    </div>
                    <p className="text-sm text-[#819888] mt-4">Tap any pillar above to see exactly what was measured and what to do next.</p>
                </section>
            </main>
        );
    }

    // ---------- QUESTION VIEW ----------
    const isFirstTime = !user?.assessment_result;
    return (
        <main className="container-page py-12 max-w-2xl fade-up" data-testid="assessment-question-view">
            {isFirstTime && idx === 0 && (
                <div
                    className="card-rw p-5 mb-6 fade-up"
                    style={{ borderLeft: "6px solid #2D5D4E", background: "#F2F7F4" }}
                    data-testid="assessment-welcome-banner"
                >
                    <p className="label-eyebrow mb-1" style={{ color: "#2D5D4E" }}>Welcome · 5 minutes</p>
                    <p className="text-[#1C2321] leading-relaxed mb-3">
                        Before the lessons unlock, take this short placement check so we can start you in exactly the right place.
                        Every prompt can be read aloud. There are no trick questions — guess if you're unsure, and let the answers find your level.
                    </p>
                    <ListenButton
                        text="Hi, I'm here to walk you through this. It's quick — about five minutes. There are no trick questions, and every prompt has a Read this to me button. Take your time. You've got this."
                        label="Hear a short hello first"
                        size="sm"
                        testId="assessment-welcome-listen"
                    />
                </div>
            )}
            <div className="flex items-center justify-between mb-3">
                <p className="label-eyebrow">Placement · Question {idx + 1} of {questions.length}</p>
                <p className="text-sm text-[#819888]">
                    {Math.round(((idx) / questions.length) * 100)}% through
                </p>
            </div>

            <div className="w-full h-1.5 bg-[#E5E7E2] rounded-full mb-8 overflow-hidden">
                <div
                    className="h-full bg-[#2D5D4E] transition-all duration-300"
                    style={{ width: `${((idx) / questions.length) * 100}%` }}
                />
            </div>

            <div className="card-rw p-8 mb-6" data-testid={`assessment-question-${current.id}`}>
                <p className="text-lg text-[#1C2321] mb-4">{current.prompt}</p>

                {/* Listen button for the prompt — important for non-readers */}
                <div className="mb-5">
                    <ListenButton text={current.audio_text || current.prompt} testId={`assessment-listen-${current.id}`} size="sm" label="Read this to me" />
                </div>

                {/* Type-specific render */}
                {current.word && (
                    <div className="bg-[#FAF9F6] border border-[#E5E7E2] rounded-lg p-6 text-center mb-2">
                        <p className="text-5xl font-semibold text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{current.word}</p>
                    </div>
                )}

                {current.passage && (
                    <article className="bg-[#FAF9F6] border border-[#E5E7E2] rounded-lg p-6 mb-3" data-testid={`assessment-passage-${current.id}`}>
                        <p className="text-lg leading-[1.7] text-[#1C2321]">{current.passage}</p>
                        <div className="mt-3">
                            <ListenButton text={current.passage} testId={`assessment-passage-listen-${current.id}`} size="sm" label="Read the story to me" />
                        </div>
                    </article>
                )}

                {current.question && (
                    <p className="text-lg font-medium text-[#1C2321] mt-4 mb-2">{current.question}</p>
                )}
            </div>

            <div className="space-y-3 mb-8" data-testid="assessment-options">
                {current.options.map((opt, oi) => {
                    const selected = picked === oi;
                    return (
                        <button
                            key={oi}
                            onClick={() => pick(oi)}
                            data-testid={`assessment-option-${current.id}-${oi}`}
                            className={`w-full text-left px-5 py-4 rounded-lg border transition-all text-lg ${
                                selected
                                    ? "border-[#2D5D4E] bg-[#E0EDE6] text-[#1C2321]"
                                    : "border-[#E5E7E2] bg-white text-[#1C2321] hover:bg-[#FAF9F6]"
                            }`}
                        >
                            <span className="inline-block w-7 h-7 mr-3 rounded-full border border-[#E5E7E2] text-center text-sm leading-7 font-medium align-middle" style={{ background: selected ? "#2D5D4E" : "transparent", color: selected ? "#FAF9F6" : "#515B57" }}>
                                {String.fromCharCode(65 + oi)}
                            </span>
                            {opt}
                        </button>
                    );
                })}
            </div>

            <div className="flex justify-between items-center gap-3">
                <button
                    onClick={() => setIdx(Math.max(0, idx - 1))}
                    disabled={idx === 0}
                    className="px-5 py-3 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] font-medium hover:bg-[#E8E2D2] disabled:opacity-40"
                    data-testid="assessment-prev"
                >
                    Back
                </button>
                {isLast ? (
                    <button
                        onClick={submit}
                        disabled={submitting || picked === undefined}
                        data-testid="assessment-submit"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237] disabled:opacity-50"
                    >
                        <Sparkles className="w-5 h-5" />
                        {submitting ? "Scoring…" : "See my placement"}
                    </button>
                ) : (
                    <button
                        onClick={next}
                        disabled={picked === undefined}
                        data-testid="assessment-next"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237] disabled:opacity-50"
                    >
                        Next <ArrowRight className="w-5 h-5" />
                    </button>
                )}
            </div>
        </main>
    );
}
